import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Camera, CameraOff, Shuffle, Smartphone, ArrowRight, Building2 } from "lucide-react";
import { ScanViewport } from "@/components/scan/ScanViewport";
import { ScanResultOverlay } from "@/components/scan/ScanResultOverlay";
import { KpiNumber } from "@/components/common/KpiNumber";
import { DoItSignature } from "@/components/brand/Brand";
import { useDemo } from "@/lib/demo/store";
import { useScanFlow } from "@/lib/demo/useScanFlow";
import { companyStats } from "@/lib/demo/analytics";
import { DEVICES } from "@/lib/demo/seed";
import { fullName, clockNow, fmt, byRecent } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";
import { LangSwitcher } from "@/components/i18n/LangSwitcher";

const DURATION = 1800;
const COMPANY = "bosch";

function toOverlay(r, t) {
  if (!r) return null;
  const p = r.participant;
  if (r.status === "invalid") return { tone: "error", icon: X, title: t("QR NO VÁLIDO"), note: t("Código no reconocido"), time: clockNow(), testId: "company-result-invalid" };
  if (r.status === "duplicate") {
    return { tone: "warning", icon: Check, title: t(p.gender === "F" ? "✓ CANDIDATA YA REGISTRADA" : "✓ CANDIDATO YA REGISTRADO"), name: fullName(p), note: t("Currículum recibido anteriormente · {time}", { time: r.interaction.time }), testId: "company-result-duplicate" };
  }
  return { tone: "success", icon: Check, title: t("✓ CURRÍCULUM RECIBIDO"), name: fullName(p).toUpperCase(), lines: [t(p.titulacion), p.centro], time: r.interaction.time, note: t("Perfil añadido a tus candidatos"), testId: "company-result-received" };
}

const SidePanel = ({ stats, device, recent }) => {
  const { t } = useI18n();
  return (
  <aside className="hidden w-[400px] space-y-5 pt-6 lg:block" data-testid="scanner-side-panel">
    <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Scanner empresa · mobile-first")}</p><LangSwitcher testPrefix="scanner-lang" /></div>
    <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">{t("ESCANEAR → RECIBIR CV → SIGUIENTE")}</h1>
    <p className="text-slate-600">{t("Sin formularios ni confirmaciones. Cada lectura comparte el perfil y el currículum con la empresa en ~1 segundo.")}</p>
    <div className="rounded-[24px] border border-slate-200 bg-white p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("Un stand, varios reclutadores")}</p>
      <ul className="mt-4 space-y-2">
        {stats.devices.map((d) => (
          <li key={d.id} className={`flex items-center justify-between rounded-xl px-4 py-3 ${d.id === device ? "bg-upv-light" : "bg-slate-50"}`} data-testid={`scanner-device-row-${d.id}`}>
            <span className="flex items-center gap-3 text-sm font-semibold"><Smartphone className="h-4 w-4 text-upv" /> {d.label}{d.id === device && <span className="rounded-full bg-upv px-2 py-0.5 text-[10px] text-white">{t("este")}</span>}</span>
            <span className="font-mono text-sm font-bold">{fmt(d.value)}</span>
          </li>
        ))}
      </ul>
      <Link to="/empresa/portal?s=candidatos" className="mt-4 flex items-center justify-between rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white" data-testid="scanner-portal-link">
        <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> {t("Todos alimentan el mismo portal Bosch")}</span><ArrowRight className="h-4 w-4" />
      </Link>
    </div>
    <div className="rounded-[24px] border border-slate-200 bg-white p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("Últimos candidatos recibidos")}</p>
      <ul className="mt-3 space-y-2">
        {recent.map(({ i, p }) => (
          <li key={i.id} className="flex items-center gap-3 text-sm" data-testid={`scanner-recent-${i.id}`}>
            <span className="font-mono font-bold">{i.time}</span><span className="truncate">{fullName(p)}</span><span className="ml-auto text-xs text-slate-400">{i.deviceId || "Web"}</span>
          </li>
        ))}
      </ul>
    </div>
    <DoItSignature />
  </aside>
  );
};

export default function EmpresaScanner() {
  const [params] = useSearchParams();
  const { state, recordCompanyScan, activeParticipant, participantsById, navHidden } = useDemo();
  const [device, setDevice] = useState(params.get("device") || "RD-01");
  const [camera, setCamera] = useState(false);
  const { t } = useI18n();
  const stats = useMemo(() => companyStats(state, COMPANY), [state]);
  const process = useCallback((token) => recordCompanyScan(COMPANY, token, device), [recordCompanyScan, device]);
  const { phase, result, trigger } = useScanFlow(process, DURATION);
  const recent = state.interactions.filter((i) => i.companyId === COMPANY && i.day === 2).sort(byRecent).slice(0, 5).map((i) => ({ i, p: participantsById[i.participantId] })).filter((r) => r.p);

  const cycleDevice = () => setDevice((d) => DEVICES[(DEVICES.findIndex((x) => x.id === d) + 1) % DEVICES.length].id);
  const simulateOther = () => {
    const done = new Set(state.interactions.filter((i) => i.companyId === COMPANY).map((i) => i.participantId));
    const pool = state.participants.filter((p) => !done.has(p.id) && p.id !== activeParticipant.id);
    const p = pool[Math.floor(Math.random() * pool.length)] || state.participants[1];
    trigger(p.token);
  };
  const deviceLabel = DEVICES.find((d) => d.id === device)?.label || device;

  return (
    <div className="min-h-screen bg-slate-100 lg:py-10" data-testid="company-scanner-page">
      <div className="mx-auto flex max-w-6xl justify-center gap-16">
        <div className={`relative flex min-h-[100dvh] w-full flex-col overflow-hidden bg-slate-950 text-white lg:h-[840px] lg:min-h-0 lg:w-[410px] lg:rounded-[48px] lg:border-[10px] lg:border-slate-900 lg:shadow-2xl ${navHidden ? "pb-6" : "pb-24"} lg:pb-6`} data-testid="company-scanner-phone">
          <header className="px-6 pt-7">
            <div className="flex items-center justify-between">
              <p className="font-display text-3xl font-extrabold tracking-tight" data-testid="company-scanner-name">BOSCH</p>
              <button onClick={cycleDevice} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-semibold text-white/80 transition-colors hover:bg-white/10" data-testid="company-scanner-device-button">
                <Smartphone className="h-3.5 w-3.5" /> {deviceLabel.replace("Recruiter ", "")}
              </button>
            </div>
            <p className="mt-0.5 text-sm text-white/50">Foro de Empleo UPV 2027</p>
            <div className="mt-5 flex items-center gap-3 rounded-2xl bg-white/[0.06] px-4 py-3">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative h-2.5 w-2.5 rounded-full bg-emerald-400" /></span>
              <KpiNumber value={stats.today} testId="company-scanner-today-count" className="font-display text-2xl font-bold" />
              <span className="text-sm text-white/70">{t("candidatos recibidos hoy")}</span>
            </div>
          </header>
          <div className="flex flex-1 flex-col justify-center px-6 py-6">
            <ScanViewport phase={phase} cameraOn={camera} onDecode={(t) => trigger(t, { fromCamera: true })} />
            <p className="mt-6 text-center font-display text-xl font-extrabold tracking-tight" data-testid="company-scanner-instruction">{t("ESCANEA EL QR DEL CANDIDATO")}</p>
            <p className="mt-1.5 text-center text-sm text-white/55">{t("Recibe su perfil profesional y currículum al instante.")}</p>
          </div>
          <div className="space-y-3 px-6">
            <button onClick={() => trigger(activeParticipant.token)} disabled={phase !== "idle"} className="flex h-16 w-full items-center justify-center rounded-2xl bg-upv font-display text-lg font-bold tracking-wider text-white transition-[background-color,transform] active:scale-[0.98] hover:bg-[#a10f1a] disabled:opacity-60" data-testid="company-scanner-simulate-button">
              {t("SIMULAR LECTURA QR")}
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={simulateOther} disabled={phase !== "idle"} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-white/[0.06] text-xs font-semibold text-white/80 transition-colors hover:bg-white/10 disabled:opacity-60" data-testid="company-scanner-simulate-other-button"><Shuffle className="h-3.5 w-3.5" /> {t("Otro candidato")}</button>
              <button onClick={() => setCamera((c) => !c)} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-white/[0.06] text-xs font-semibold text-white/80 transition-colors hover:bg-white/10" data-testid="company-scanner-camera-toggle">{camera ? <CameraOff className="h-3.5 w-3.5" /> : <Camera className="h-3.5 w-3.5" />} {t(camera ? "Apagar cámara" : "Usar cámara")}</button>
            </div>
          </div>
          <ScanResultOverlay data={phase === "result" ? toOverlay(result, t) : null} duration={DURATION} contained compact />
        </div>
        <SidePanel stats={stats} device={device} recent={recent} />
      </div>
    </div>
  );
}
